import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCurrentUser, signIn, signOut, fetchAuthSession, confirmSignIn, SignInInput } from 'aws-amplify/auth';

interface User {
  username: string;
  userId: string;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsPasswordChange: boolean;
  login: (username: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);

  useEffect(() => {
    checkUser();
  }, []);

  async function checkUser() {
    try {
      const currentUser = await getCurrentUser();
      setUser({
        username: currentUser.username,
        userId: currentUser.userId,
      });
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  async function login(username: string, password: string) {
    try {
      const signInInput: SignInInput = {
        username,
        password,
      };
      
      const { isSignedIn, nextStep } = await signIn(signInInput);
      
      if (isSignedIn) {
        setNeedsPasswordChange(false);
        await checkUser();
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // User needs to set a new password
        setNeedsPasswordChange(true);
        throw new Error('NEW_PASSWORD_REQUIRED');
      } else {
        // Handle other multi-step sign-in (MFA, etc.)
        console.log('Next step:', nextStep);
        throw new Error(`Additional step required: ${nextStep.signInStep}`);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async function completeNewPassword(newPassword: string) {
    try {
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: newPassword,
      });
      
      if (isSignedIn) {
        setNeedsPasswordChange(false);
        await checkUser();
      }
    } catch (error: any) {
      console.error('Password change error:', error);
      throw new Error(error.message || 'Failed to set new password');
    }
  }

  async function logout() {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  async function getToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Get token error:', error);
      return null;
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        needsPasswordChange,
        login,
        completeNewPassword,
        logout,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

